import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiMail } from "react-icons/fi";
import { usePublicLang } from "../../../../config/i18n/public";
import { trackAnalyticsEvent } from "../../../../utils/analytics";

const API = process.env.REACT_APP_API_URL;

export default function ContactFooterNewsletterCard({ newsletter }) {
  const { lang } = usePublicLang();
  const [email, setEmail] = useState("");
  const [launchConsent, setLaunchConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  if (!newsletter) {
    return null;
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus("submitting");
    setMessage("");

    try {
      const res = await fetch(`${API}/newsletter-subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, language: lang }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || newsletter.errorMessage);
      }

      setEmail("");
      setLaunchConsent(false);
      setStatus("success");
      setMessage(newsletter.successMessage);
      void trackAnalyticsEvent("newsletter_signup", { form_location: "b2b_footer" });
    } catch (err) {
      setStatus("error");
      setMessage(err.message || newsletter.errorMessage);
    }
  };

  return (
    <div className="mt-[1.3rem] rounded-[1.1rem] border border-[rgba(255,255,255,0.08)] bg-[linear-gradient(135deg,rgba(242,246,255,0.98)_0%,rgba(232,240,255,0.92)_42%,rgba(224,235,255,0.94)_100%)] px-[0.95rem] pb-[0.85rem] pt-[0.95rem] text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_16px_34px_rgba(59,85,168,0.14)]">
      <div className="relative mb-[0.55rem] inline-flex h-[3.7rem] w-[3.7rem] items-center justify-center" aria-hidden="true">
        {/* The render is portrait where the old mascot was square, so it is
            sized by height and left to find its own width. */}
        <img
          src="/steffi/steffi-hearts.webp"
          alt=""
          className="h-[3.4rem] w-auto drop-shadow-[0_12px_22px_rgba(71,108,224,0.2)]"
        />
        <span className="absolute right-[0.1rem] top-[0.1rem] inline-flex h-[1.2rem] w-[1.2rem] items-center justify-center rounded-full bg-white text-[0.68rem] text-[#5d8dff] shadow-[0_8px_14px_rgba(61,86,168,0.12)]">
          <FiMail />
        </span>
      </div>

      <h3 className="m-0 text-[1.12rem] font-extrabold tracking-[-0.02em] text-[#1a2347]">
        {newsletter.title}
      </h3>
      <p className="mx-auto mt-[0.38rem] max-w-[16rem] text-[0.8rem] leading-[1.45] text-[rgba(26,35,71,0.68)]">
        {newsletter.description}
      </p>

      <form
        className="mt-[0.68rem] rounded-[1.3rem] bg-[rgba(255,255,255,0.72)] p-[0.34rem] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]"
        onSubmit={onSubmit}
      >
        <div className="flex items-center gap-[0.55rem] max-sm:flex-col max-sm:items-stretch">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={newsletter.placeholder}
            className="min-w-0 flex-1 border-0 bg-transparent px-[0.72rem] text-[0.84rem] text-[#1d2953] outline-none placeholder:text-[rgba(30,41,82,0.38)] max-sm:min-h-[2.9rem] max-sm:px-[0.9rem]"
            required
          />
          <button
            type="submit"
            className={`inline-flex min-h-[2.4rem] items-center justify-center rounded-full border-0 bg-[#4285f4] px-[0.95rem] text-[0.84rem] font-bold tracking-[-0.01em] text-white shadow-[0_10px_20px_rgba(66,133,244,0.2)] transition duration-200 hover:-translate-y-px hover:bg-[#357ae8] max-sm:w-full ${
              status === "submitting" ? "cursor-wait opacity-80" : "cursor-pointer"
            }`}
            disabled={status === "submitting"}
          >
            {status === "submitting" ? newsletter.submittingLabel : newsletter.label}
          </button>
        </div>
        <label className="mt-[0.45rem] flex items-start gap-[0.42rem] px-[0.55rem] pb-[0.25rem] text-left text-[0.68rem] leading-[1.35] text-[rgba(26,35,71,0.68)]">
          <input
            type="checkbox"
            checked={launchConsent}
            onChange={(event) => setLaunchConsent(event.target.checked)}
            className="mt-[0.15rem] h-[0.82rem] w-[0.82rem] shrink-0 accent-[#4285f4]"
            required
          />
          <span>
            {newsletter.consentLabel} {newsletter.privacyPrefix}{" "}
            <Link
              to="/datenschutzhinweis"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#315fbe] underline-offset-2 hover:underline"
            >
              {newsletter.privacyLinkLabel}
            </Link>
            .
          </span>
        </label>
      </form>

      {message ? (
        <p
          className={`mt-[0.85rem] text-[0.82rem] leading-[1.5] ${
            status === "error" ? "text-[#b42318]" : "text-[rgba(26,35,71,0.72)]"
          }`}
        >
          {message}
        </p>
      ) : newsletter.note ? (
        <p className="mt-[0.85rem] text-[0.82rem] leading-[1.5] text-[rgba(26,35,71,0.52)]">
          {newsletter.note}
        </p>
      ) : null}
    </div>
  );
}
